import React, { Component } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
    Avatar,
    Button,
    CssBaseline,
    TextField,
    Grid,
    Typography,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WebhookIcon from '@mui/icons-material/Webhook';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SendIcon from '@mui/icons-material/Send';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import { Overlay } from '../../styles/global';
import Api from '../../services/api';
import * as WebhookService from '../../services/webhook.service';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import { connect } from 'react-redux';
import Footer from '../../components/footer';
import { browserHistory } from '../../helpers/history';
import { FormattedMessage, injectIntl } from 'react-intl';
import Title from '../../components/title-name';
import WebhookHeadersEditor from '../../components/webhook-headers-editor';
import WebhookAuthConfig from '../../components/webhook-auth-config';
import WebhookEventsSelector from '../../components/webhook-events-selector';
import WebhookTestModal from '../../components/webhook-test-modal';

const SIGNING_ALGORITHMS = ['HMACSHA256', 'HMACSHA512'];

class WebhookRegistration extends Component {

    constructor(props) {
        super(props);

        const { userSession } = this.props;

        if (userSession == null || userSession.user == null) {
            Api.logoff();
        }

        this.initialState = {
            name: '',
            description: '',
            endpointUrl: '',
            httpMethod: 'POST',
            requestFormat: 'JSON',
            authType: 'NONE',
            authConfig: {},
            signingSecret: '',
            signingAlgorithm: 'HMACSHA256',
            customHeaders: [],
            isActive: true,
            rateLimitPerMinute: 60,
            maxRetryAttempts: 5,
            initialRetryIntervalSeconds: 60
        };

        this.state = {
            item: this.initialState,
            loading: false,
            openNotification: false,
            notificationVariant: "error",
            notificationMessage: "",
            eventTypes: [],
            selectedEventIds: [],
            showSecret: false,
            testModalOpen: false,
            expandedSections: {
                basic: true,
                authentication: false,
                headers: false,
                security: false,
                retry: false,
                events: true
            }
        };

        this.handleInputChange = this.handleInputChange.bind(this);
        this.closeNotification = this.closeNotification.bind(this);
        this.handleAuthChange = this.handleAuthChange.bind(this);
        this.handleHeadersChange = this.handleHeadersChange.bind(this);
        this.handleEventsChange = this.handleEventsChange.bind(this);
        this.generateSecret = this.generateSecret.bind(this);
    }

    componentDidMount() {
        this.loadEventTypes();
    }

    async loadEventTypes() {
        this.setState({ loading: true });
        const result = await WebhookService.getEventTypes();
        if (result.success) {
            this.setState({ loading: false, eventTypes: result.data });
        } else {
            const intl = this.props.intl;
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: result.errors && result.errors[0] ? result.errors[0] : intl.formatMessage({ id: "process.error" })
            });
        }
    }

    handleInputChange(e) {
        const name = e.target.name;
        const value = e.target.value;

        this.setState(prevState => ({
            item: {
                ...prevState.item,
                [name]: value
            }
        }));
    }

    handleAuthChange(field, value) {
        this.setState(prevState => ({
            item: {
                ...prevState.item,
                [field]: value
            }
        }));
    }

    handleHeadersChange(headers) {
        this.setState(prevState => ({
            item: {
                ...prevState.item,
                customHeaders: headers
            }
        }));
    }

    handleEventsChange(selectedIds) {
        this.setState({ selectedEventIds: selectedIds });
    }

    generateSecret() {
        const secret = WebhookService.generateLocalSecret();
        this.setState(prevState => ({
            item: {
                ...prevState.item,
                signingSecret: secret
            }
        }));
    }

    toggleSection = (section) => {
        this.setState(prevState => ({
            expandedSections: {
                ...prevState.expandedSections,
                [section]: !prevState.expandedSections[section]
            }
        }));
    }

    validateForm() {
        const { item, selectedEventIds } = this.state;
        const intl = this.props.intl;

        if (!item.name.trim()) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.name.required" })
            });
            return false;
        }

        if (!item.endpointUrl.trim()) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.url.required" })
            });
            return false;
        }

        // Validar URL
        try {
            new URL(item.endpointUrl);
        } catch (e) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.url.invalid" })
            });
            return false;
        }

        if (selectedEventIds.length === 0) {
            this.setState({
                openNotification: true,
                notificationVariant: "error",
                notificationMessage: intl.formatMessage({ id: "webhook.validation.events.required" })
            });
            return false;
        }

        return true;
    }

    handleSubmit = async e => {
        e.preventDefault();

        if (!this.validateForm()) return;

        this.setState({ loading: true });

        const { item, selectedEventIds } = this.state;

        // Converter customHeaders de array [{key, value}] para objeto {key: value}
        const customHeadersObj = {};
        if (item.customHeaders && item.customHeaders.length > 0) {
            item.customHeaders.forEach(h => {
                if (h.key) customHeadersObj[h.key] = h.value || '';
            });
        }

        const webhookData = {
            name: item.name,
            description: item.description,
            endpointUrl: item.endpointUrl,
            httpMethod: 'POST',
            requestFormat: 'JSON',
            authType: item.authType || 'NONE',
            authConfig: item.authType && item.authType !== 'NONE' ? item.authConfig : null,
            signingSecret: item.signingSecret || null,
            signingAlgorithm: item.signingAlgorithm || 'HMACSHA256',
            customHeaders: Object.keys(customHeadersObj).length > 0 ? customHeadersObj : null,
            rateLimitPerMinute: parseInt(item.rateLimitPerMinute) || 60,
            maxRetryAttempts: parseInt(item.maxRetryAttempts) || 5,
            initialRetryIntervalSeconds: parseInt(item.initialRetryIntervalSeconds) || 60,
            eventTypeIds: selectedEventIds
        };

        const result = await WebhookService.createWebhook(webhookData);

        if (result.success) {
            const intl = this.props.intl;
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: "success",
                notificationMessage: intl.formatMessage({ id: "webhook.created.success" })
            });

            setTimeout(() => {
                browserHistory.push(`/${this.props.match.params.lang}/webhooks`);
            }, 1500);
        } else {
            const intl = this.props.intl;
            this.setState({
                loading: false,
                openNotification: true,
                notificationVariant: 'error',
                notificationMessage: result.errors && result.errors[0] ? result.errors[0] : intl.formatMessage({ id: "process.error" })
            });
        }
    };

    closeNotification() {
        this.setState({ openNotification: false });
    }

    render() {
        const { classes, headerMenu, actionsHeaderMenu } = this.props;
        const { item, expandedSections } = this.state;
        const intl = this.props.intl;

        if (Api == null) {
            var l = this.props.match.params.lang;
            document.location.href = `/${l}/unavailable`;
        }

        return (
            <div className={classes.root}>
                <HeaderMenu />
                <main
                    className={clsx(classes.content, {
                        [classes.contentShift]: headerMenu.open,
                    }, classes.actionsContent, {
                        [classes.actionscontentShift]: actionsHeaderMenu.open,
                    })}
                >
                    <div className={classes.drawerHeader} />

                    <Container component="main" maxWidth="md">
                        <CssBaseline />
                        <div className={classes.paper} style={{ marginTop: 0 }}>
                            <Grid container spacing={6}>
                                <Grid item xs={12} sm={4}>
                                    <Button variant="outlined" color="primary" onClick={browserHistory.goBack}>
                                        {<FormattedMessage id="back" />}
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={8} />
                            </Grid>

                            <Avatar className={classes.avatar}>
                                <WebhookIcon />
                            </Avatar>
                            <Typography component="h1" variant="h5">
                                {<FormattedMessage id="webhook.registration" />}
                                <Title />
                            </Typography>

                            <form name="webhookForm" className={classes.form} onSubmit={this.handleSubmit}>
                                {/* Informacoes Basicas */}
                                <Accordion expanded={expandedSections.basic} onChange={() => this.toggleSection('basic')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.basic" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    name="name"
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "name" })}
                                                    value={item.name}
                                                    onChange={this.handleInputChange}
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    name="description"
                                                    variant="outlined"
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    label={intl.formatMessage({ id: "description" })}
                                                    value={item.description}
                                                    onChange={this.handleInputChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    name="endpointUrl"
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "webhook.endpoint.url" })}
                                                    value={item.endpointUrl}
                                                    onChange={this.handleInputChange}
                                                    placeholder="https://api.exemplo.com/webhook"
                                                    required
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "webhook.http.method" })}
                                                    value="POST"
                                                    disabled
                                                    helperText={intl.formatMessage({ id: "webhook.http.method.fixed" })}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth
                                                    label={intl.formatMessage({ id: "webhook.request.format" })}
                                                    value="JSON"
                                                    disabled
                                                    helperText={intl.formatMessage({ id: "webhook.request.format.fixed" })}
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Autenticacao */}
                                <Accordion expanded={expandedSections.authentication} onChange={() => this.toggleSection('authentication')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.authentication" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <WebhookAuthConfig
                                            authType={item.authType}
                                            authConfig={item.authConfig}
                                            onChange={this.handleAuthChange}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Headers */}
                                <Accordion expanded={expandedSections.headers} onChange={() => this.toggleSection('headers')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.headers" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <WebhookHeadersEditor
                                            headers={item.customHeaders}
                                            onChange={this.handleHeadersChange}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Seguranca */}
                                <Accordion expanded={expandedSections.security} onChange={() => this.toggleSection('security')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.security" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={8}>
                                                <TextField
                                                    name="signingSecret"
                                                    variant="outlined"
                                                    fullWidth
                                                    type={this.state.showSecret ? 'text' : 'password'}
                                                    label={intl.formatMessage({ id: "webhook.signing.secret" })}
                                                    value={item.signingSecret}
                                                    onChange={this.handleInputChange}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => this.setState({ showSecret: !this.state.showSecret })}
                                                                    edge="end"
                                                                >
                                                                    {this.state.showSecret ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    startIcon={<AutorenewIcon />}
                                                    onClick={this.generateSecret}
                                                    style={{ height: '56px' }}
                                                >
                                                    {intl.formatMessage({ id: "webhook.generate.secret" })}
                                                </Button>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth variant="outlined">
                                                    <InputLabel>{intl.formatMessage({ id: "webhook.signing.algorithm" })}</InputLabel>
                                                    <Select
                                                        name="signingAlgorithm"
                                                        value={item.signingAlgorithm}
                                                        onChange={this.handleInputChange}
                                                        label={intl.formatMessage({ id: "webhook.signing.algorithm" })}
                                                    >
                                                        {SIGNING_ALGORITHMS.map(alg => (
                                                            <MenuItem key={alg} value={alg}>{alg}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Retry e Rate Limit */}
                                <Accordion expanded={expandedSections.retry} onChange={() => this.toggleSection('retry')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.retry" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    name="maxRetryAttempts"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    label={intl.formatMessage({ id: "webhook.max.retry" })}
                                                    value={item.maxRetryAttempts}
                                                    onChange={this.handleInputChange}
                                                    inputProps={{ min: 0, max: 10 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    name="initialRetryIntervalSeconds"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    label={intl.formatMessage({ id: "webhook.initial.retry.interval" })}
                                                    value={item.initialRetryIntervalSeconds}
                                                    onChange={this.handleInputChange}
                                                    inputProps={{ min: 1 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    name="rateLimitPerMinute"
                                                    variant="outlined"
                                                    fullWidth
                                                    type="number"
                                                    label={intl.formatMessage({ id: "webhook.rate.limit" })}
                                                    value={item.rateLimitPerMinute}
                                                    onChange={this.handleInputChange}
                                                    inputProps={{ min: 1 }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {/* Eventos */}
                                <Accordion expanded={expandedSections.events} onChange={() => this.toggleSection('events')}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: "webhook.section.events" })}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <WebhookEventsSelector
                                            eventTypes={this.state.eventTypes}
                                            selectedEventIds={this.state.selectedEventIds}
                                            onChange={this.handleEventsChange}
                                        />
                                    </AccordionDetails>
                                </Accordion>

                                {/* Botoes */}
                                <Grid container spacing={2} style={{ marginTop: 24 }} justifyContent="flex-end">
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            fullWidth
                                            variant='outlined'
                                            color='secondary'
                                            startIcon={<SendIcon />}
                                            onClick={() => this.setState({ testModalOpen: true })}
                                            disabled={!this.state.item.endpointUrl || this.state.selectedEventIds.length === 0}
                                        >
                                            {intl.formatMessage({ id: "webhook.test" })}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            fullWidth
                                            variant='outlined'
                                            color='primary'
                                            onClick={browserHistory.goBack}
                                        >
                                            {<FormattedMessage id="cancel" />}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            type='submit'
                                            fullWidth
                                            variant='contained'
                                            color='primary'
                                        >
                                            {<FormattedMessage id="save" />}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        </div>

                        {this.state.loading && (
                            <Overlay>
                                <CircularProgress color="secondary" />
                            </Overlay>
                        )}

                        <CustomizedSnackbars
                            variant={this.state.notificationVariant}
                            message={this.state.notificationMessage}
                            isOpen={this.state.openNotification}
                            toClose={this.closeNotification}
                        />

                        <WebhookTestModal
                            isOpen={this.state.testModalOpen}
                            handleClose={() => this.setState({ testModalOpen: false })}
                            webhookConfig={{
                                endpointUrl: this.state.item.endpointUrl,
                                httpMethod: 'POST',
                                requestFormat: 'JSON',
                                authType: this.state.item.authType,
                                authConfig: this.state.item.authConfig,
                                customHeaders: this.state.item.customHeaders,
                                signingSecret: this.state.item.signingSecret,
                                signingAlgorithm: this.state.item.signingAlgorithm
                            }}
                            webhookName={this.state.item.name || intl.formatMessage({ id: "webhook.new" })}
                            eventTypes={this.state.eventTypes}
                            selectedEventIds={this.state.selectedEventIds}
                        />
                    </Container>
                </main>
                <Footer />
            </div>
        );
    }
}

WebhookRegistration.propTypes = {
    classes: PropTypes.object.isRequired
};

const mapStateToProps = state => ({
    actionsHeaderMenu: state.actionsHeaderMenu,
    headerMenu: state.headerMenu,
    userSession: state.userSession,
});

export default injectIntl(connect(mapStateToProps)(withStyles(Styles)(WebhookRegistration)));
